import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useAppContext } from "../../../../../hooks/useAppContext";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { cn } from "../../../../../lib/utils";
import { User } from "../../../../../types/VenueGroup";
import { Input } from "../../../../ui/input";

const MONGO_ID_REGEX = /^[0-9a-f]{24}$/;
const MAX_SUGGESTIONS = 10;

interface MentionSuggestion {
  username: string;
  fullName: string;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  className?: string;
}

const isMongoId = (id: string): boolean => MONGO_ID_REGEX.test(id);

const getUsersForVenue = (users: User[], venueId: string): User[] =>
  users.filter(
    (user) =>
      user.venues === true ||
      (Array.isArray(user.venues) && user.venues.includes(venueId))
  );

const escapeRegex = (str: string): string =>
  str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getFilteredSuggestions = (
  users: User[],
  searchText: string
): MentionSuggestion[] => {
  const validUsers = users.filter(
    (user) => user.username && !isMongoId(user.username)
  );

  const suggestions: MentionSuggestion[] = [];

  for (const user of validUsers) {
    if (suggestions.length >= MAX_SUGGESTIONS) break;

    const suggestion: MentionSuggestion = {
      username: user.username,
      fullName: user.full_name
    };

    if (!searchText) {
      suggestions.push(suggestion);
      continue;
    }

    try {
      const regex = new RegExp(escapeRegex(searchText), "i");
      if (regex.test(suggestion.username) || regex.test(suggestion.fullName)) {
        suggestions.push(suggestion);
      }
    } catch {
      const lower = searchText.toLowerCase();
      if (
        suggestion.username.toLowerCase().includes(lower) ||
        suggestion.fullName.toLowerCase().includes(lower)
      ) {
        suggestions.push(suggestion);
      }
    }
  }

  return suggestions;
};

const MentionInput = ({
  value,
  onChange,
  placeholder,
  maxLength,
  className
}: MentionInputProps) => {
  const { venueGroupConfig } = useAppContext();
  const { venueConfig } = useDiaryContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const lastValueRef = useRef(value);
  const [suggestions, setSuggestions] = useState<MentionSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [mentionStart, setMentionStart] = useState<number | null>(null);

  const venueUsers = useMemo(
    () =>
      venueGroupConfig?.users && venueConfig?._id
        ? getUsersForVenue(venueGroupConfig.users, venueConfig._id)
        : [],
    [venueGroupConfig?.users, venueConfig?._id]
  );

  const closeSuggestions = useCallback(() => {
    setSuggestions([]);
    setActiveIndex(-1);
    setMentionStart(null);
  }, []);

  const selectSuggestion = useCallback(
    (index: number) => {
      const selected = suggestions[index];
      if (!selected || mentionStart === null) return;

      const currentValue = lastValueRef.current;
      let wordEnd = mentionStart;
      while (
        wordEnd < currentValue.length &&
        currentValue.charAt(wordEnd) !== " "
      ) {
        wordEnd++;
      }

      const newValue = `${currentValue.substring(0, mentionStart)}@${selected.username} ${currentValue.substring(wordEnd)}`;

      onChange(newValue);
      closeSuggestions();

      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    },
    [suggestions, mentionStart, onChange, closeSuggestions]
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.target.value;
    lastValueRef.current = newValue;
    onChange(newValue);

    const caretPos = e.target.selectionEnd ?? newValue.length;
    let wordStart = caretPos;
    let wordEnd = caretPos;

    while (wordStart > 0 && newValue.charAt(wordStart - 1) !== " ") {
      wordStart--;
    }
    while (wordEnd < newValue.length && newValue.charAt(wordEnd) !== " ") {
      wordEnd++;
    }

    const currentWord = newValue.substring(wordStart, wordEnd);

    if (currentWord.startsWith("@")) {
      const searchText = currentWord.substring(1);
      const matches = getFilteredSuggestions(venueUsers, searchText);
      setSuggestions(matches);
      setActiveIndex(matches.length > 0 ? 0 : -1);
      setMentionStart(wordStart);
    } else {
      closeSuggestions();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % suggestions.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex(
          (prev) => (prev - 1 + suggestions.length) % suggestions.length
        );
        break;
      case "Enter":
      case "Tab":
        if (activeIndex >= 0) {
          e.preventDefault();
          selectSuggestion(activeIndex);
        }
        break;
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        closeSuggestions();
        break;
    }
  };

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const activeItem = listRef.current.children[activeIndex] as
        | HTMLElement
        | undefined;
      activeItem?.scrollIntoView?.({ block: "nearest" });
    }
  }, [activeIndex]);

  useEffect(() => {
    if (suggestions.length === 0) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !inputRef.current?.contains(target) &&
        !listRef.current?.contains(target)
      ) {
        closeSuggestions();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [suggestions.length, closeSuggestions]);

  const isOpen = suggestions.length > 0;
  const listboxId = "mention-suggestions-listbox";

  return (
    <div className={cn("relative", className)}>
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={
          isOpen && activeIndex >= 0
            ? `mention-option-${activeIndex}`
            : undefined
        }
        aria-autocomplete="list"
      />
      {isOpen && (
        <div
          ref={listRef}
          id={listboxId}
          aria-label="User suggestions"
          className="absolute bottom-full left-0 right-0 mb-1 max-h-48 overflow-y-auto rounded-md border border-border bg-popover shadow-md z-50"
        >
          {suggestions.map((suggestion, index) => (
            <button
              type="button"
              key={suggestion.username}
              id={`mention-option-${index}`}
              data-selected={index === activeIndex}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm cursor-pointer",
                index === activeIndex
                  ? "bg-accent text-accent-foreground"
                  : "hover:bg-accent hover:text-accent-foreground"
              )}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                selectSuggestion(index);
              }}
            >
              <span className="font-medium">{suggestion.username}</span>
              <span className="text-muted-foreground text-xs">
                {suggestion.fullName}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionInput;
